import { Directive } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  UntypedFormArray,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import * as _ from 'lodash';

const requiredIfNoUserId = (
  control: AbstractControl,
): ValidationErrors | null => {
  const parent = control.parent;
  if (!parent) return null;
  const userId = parent.get('userId')?.value;
  if ((!userId || userId === ' ') && !control.value) return { required: true };
  return null;
};

@Directive()
export abstract class EaCompanyFormBuilder extends PrFormAbstract {
  generate(entity: any) {
    let options: AbstractControlOptions = {};

    let beneficialOwners: UntypedFormGroup[] = [];
    if (entity.id)
      for (let owner of entity.beneficialOwner)
        beneficialOwners.push(
          this.formBuilder.group({
            id: [owner ? owner.id : null],
            userId: [owner ? owner.userId : null],
            companyId: [owner ? owner.companyId : null],
            userFirstName: [
              owner ? owner.userFirstName : null,
              requiredIfNoUserId,
            ],
            userLastName: [
              owner ? owner.userLastName : null,
              requiredIfNoUserId,
            ],
            userSharePercentage: [
              owner ? owner.userSharePercentage : null,
              [Validators.required, Validators.min(0.01)],
            ],
            representativeFunction: [
              owner ? owner.representativeFunction : null,
              Validators.required,
            ],
          }),
        );

    const beneficialOwnerArray: UntypedFormArray = this.formBuilder.array(
      [...beneficialOwners],
      Validators.nullValidator,
    );

    beneficialOwnerArray.valueChanges.subscribe(() => {
      beneficialOwnerArray.controls.forEach(ctrl => {
        ctrl.get('userFirstName')?.updateValueAndValidity({ emitEvent: false });
        ctrl.get('userLastName')?.updateValueAndValidity({ emitEvent: false });
      });
    });

    return this.formBuilder.group(
      {
        id: [entity ? entity.id : null],
        name: [entity ? entity.name : null, Validators.required],
        activitySector: [entity ? entity.activitySector : null],
        beneficialOwner: beneficialOwnerArray,

        bilanCA: [entity ? entity.bilanCA : null],
        bilanDebt: [entity ? entity.bilanDebt : null],
        bilanEquity: [entity ? entity.bilanEquity : null],
        bilanNetResult: [entity ? entity.bilanNetResult : null],
        bilanTresory: [entity ? entity.bilanTresory : null],
        bilanTax: [entity ? entity.bilanTax : null],
        bilanTotalBilan: [entity ? entity.bilanTotalBilan : null],

        bilanCAN1: [entity ? entity.bilanCAN1 : null],
        bilanDebtN1: [entity ? entity.bilanDebtN1 : null],
        bilanEquityN1: [entity ? entity.bilanEquityN1 : null],
        bilanNetResultN1: [entity ? entity.bilanNetResultN1 : null],
        bilanTresoryN1: [entity ? entity.bilanTresoryN1 : null],
        bilanTaxN1: [entity ? entity.bilanTaxN1 : null],
        bilanTotalBilanN1: [entity ? entity.bilanTotalBilanN1 : null],

        bilanCAN2: [entity ? entity.bilanCAN2 : null],
        bilanDebtN2: [entity ? entity.bilanDebtN2 : null],
        bilanEquityN2: [entity ? entity.bilanEquityN2 : null],
        bilanNetResultN2: [entity ? entity.bilanNetResultN2 : null],
        bilanTresoryN2: [entity ? entity.bilanTresoryN2 : null],
        bilanTaxN2: [entity ? entity.bilanTaxN2 : null],
        bilanTotalBilanN2: [entity ? entity.bilanTotalBilanN2 : null],

        codeAPE: [entity ? entity.codeAPE : null],
        rcs: [entity ? entity.rcs : null],
        lieuImmatriculationRcs: [entity ? entity.lieuImmatriculationRcs : null],
        societeCotee: [
          entity && !_.isNil(entity.societeCotee) ? entity.societeCotee : false,
        ],
        societeCoteeMarche: [entity ? entity.societeCoteeMarche : null],
        codeSiret: [entity ? entity.codeSiret : null],
        email: [entity ? entity.email : null, Validators.email],
        legalForm: [entity ? entity.legalForm : null],
        representative: [
          entity ? entity.representative : null,
          Validators.required,
        ],
        representativeFunction: [
          entity ? entity.representativeFunction : null,
          Validators.required,
        ],
        startDate: [entity ? entity.startDate : null],
        taxSystem: [entity ? entity.taxSystem : null],
        addressSiegeSocial: this.formBuilder.group({
          city: [entity?.addressSiegeSocial?.city || null, Validators.required],
          country: [entity?.addressSiegeSocial?.country || null],
          street: [
            entity?.addressSiegeSocial?.street || null,
            Validators.required,
          ],
          zipCode: [
            entity?.addressSiegeSocial?.zipCode || null,
            Validators.required,
          ],
        }),
        addressSiegeAdministratif: this.formBuilder.group({
          city: [
            entity?.addressSiegeAdministratif?.city || null,
            Validators.required,
          ],
          country: [entity?.addressSiegeAdministratif?.country || null],
          street: [
            entity?.addressSiegeAdministratif?.street || null,
            Validators.required,
          ],
          zipCode: [
            entity?.addressSiegeAdministratif?.zipCode || null,
            Validators.required,
          ],
        }),
      },
      options,
    );
  }

  public createModel(owner?) {
    let model = {
      id: [owner ? owner.id : null],
      userId: [owner ? owner.userId : null],
      companyId: [owner ? owner.companyId : null],
      userFirstName: [owner ? owner.userFirstName : null, requiredIfNoUserId],
      userLastName: [owner ? owner.userLastName : null, requiredIfNoUserId],
      userSharePercentage: [
        owner ? owner.userSharePercentage : null,
        [Validators.required, Validators.min(0.01)],
      ],
      representativeFunction: [
        owner ? owner.representativeFunction : null,
        Validators.required,
      ],
    };

    return model;
  }

  extract() {
    const value = this.form.value;

    let dto: any = {
      id: this.emptyIsNull(value.id),
      activitySector: this.emptyIsNull(value.activitySector),
      beneficialOwner: this.emptyIsNull(value.beneficialOwner),
      bilanCA: this.emptyIsNull(value.bilanCA),
      bilanDebt: this.emptyIsNull(value.bilanDebt),
      bilanEquity: this.emptyIsNull(value.bilanEquity),
      bilanNetResult: this.emptyIsNull(value.bilanNetResult),
      bilanTresory: this.emptyIsNull(value.bilanTresory),
      bilanTax: this.emptyIsNull(value.bilanTax),
      bilanTotalBilan: this.emptyIsNull(value.bilanTotalBilan),

      bilanCAN1: this.emptyIsNull(value.bilanCAN1),
      bilanDebtN1: this.emptyIsNull(value.bilanDebtN1),
      bilanEquityN1: this.emptyIsNull(value.bilanEquityN1),
      bilanNetResultN1: this.emptyIsNull(value.bilanNetResultN1),
      bilanTresoryN1: this.emptyIsNull(value.bilanTresoryN1),
      bilanTaxN1: this.emptyIsNull(value.bilanTaxN1),
      bilanTotalBilanN1: this.emptyIsNull(value.bilanTotalBilanN1),

      bilanCAN2: this.emptyIsNull(value.bilanCAN2),
      bilanDebtN2: this.emptyIsNull(value.bilanDebtN2),
      bilanEquityN2: this.emptyIsNull(value.bilanEquityN2),
      bilanNetResultN2: this.emptyIsNull(value.bilanNetResultN2),
      bilanTresoryN2: this.emptyIsNull(value.bilanTresoryN2),
      bilanTaxN2: this.emptyIsNull(value.bilanTaxN2),
      bilanTotalBilanN2: this.emptyIsNull(value.bilanTotalBilanN2),

      codeAPE: this.emptyIsNull(value.codeAPE),
      codeSiret: this.emptyIsNull(value.codeSiret),
      rcs: this.emptyIsNull(value.rcs),
      lieuImmatriculationRcs: this.emptyIsNull(value.lieuImmatriculationRcs),
      societeCotee: this.emptyIsNull(value.societeCotee),
      societeCoteeMarche: this.emptyIsNull(value.societeCoteeMarche),
      legalForm: this.emptyIsNull(value.legalForm),
      email: this.emptyIsNull(value.email),
      name: this.emptyIsNull(value.name),
      representative: this.emptyIsNull(value.representative),
      representativeFunction: this.emptyIsNull(value.representativeFunction),
      startDate: this.emptyIsNull(value.startDate),
      taxSystem: this.emptyIsNull(value.taxSystem),
      addressSiegeSocial: {
        city: this.emptyIsNull(value.addressSiegeSocial.city),
        country: this.emptyIsNull(
          value.addressSiegeSocial.country.id
            ? value.addressSiegeSocial.country.id
            : value.addressSiegeSocial.country,
        ),
        street: this.emptyIsNull(value.addressSiegeSocial.street),
        street2: this.emptyIsNull(value.addressSiegeSocial.street2),
        zipCode: this.emptyIsNull(value.addressSiegeSocial.zipCode),
      },
      addressSiegeAdministratif: {
        city: this.emptyIsNull(value.addressSiegeAdministratif.city),
        country: this.emptyIsNull(
          value.addressSiegeAdministratif.country.id
            ? value.addressSiegeAdministratif.country.id
            : value.addressSiegeAdministratif.country,
        ),
        street: this.emptyIsNull(value.addressSiegeAdministratif.street),
        street2: this.emptyIsNull(value.addressSiegeAdministratif.street2),
        zipCode: this.emptyIsNull(value.addressSiegeAdministratif.zipCode),
      },
    };
    return dto;
  }
}
